import { Box, CircularProgress, Switch, TableCell, TableRow } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { FeatureSwitch } from "src/business/configuration/featureSwitch";
import {
  colorActionPrimary,
  colorActionPrimaryHover,
  colorTextMuted,
  colorTextPrimary,
  colorTextSuccess,
} from "src/design/tokens";

interface FeatureSwitchRowProps {
  featureSwitch: FeatureSwitch;
  /** This row's own toggle is in flight — it shows a spinner instead of a state label. */
  pending: boolean;
  /** Any toggle is in flight; every row is locked so two flips can't race. */
  disabled: boolean;
  onToggle: (featureSwitch: FeatureSwitch, active: boolean) => void;
  sx?: SxProps;
}

export const FeatureSwitchRow = ({
  featureSwitch,
  pending,
  disabled,
  onToggle,
  sx,
}: FeatureSwitchRowProps) => (
  <TableRow
    sx={{
      "&:last-child td": { borderBottom: 0 },
      "&:hover": { backgroundColor: "action.hover" },
      ...sx,
    }}
  >
    <TableCell sx={{ pl: 0, fontWeight: 500, color: colorTextPrimary }}>
      {featureSwitch.name}
    </TableCell>
    <TableCell
      sx={{ color: colorTextMuted, fontFamily: "monospace", fontSize: 12 }}
    >
      {featureSwitch.id}
    </TableCell>
    <TableCell align="right" sx={{ pr: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: 13,
            minWidth: 52,
            textAlign: "right",
            color: featureSwitch.active ? colorTextSuccess : colorTextMuted,
          }}
        >
          {pending ? (
            <CircularProgress size={14} sx={{ color: colorTextMuted }} />
          ) : featureSwitch.active ? (
            "On"
          ) : (
            "Off"
          )}
        </Box>
        <Switch
          checked={featureSwitch.active}
          disabled={disabled}
          onChange={(event) => onToggle(featureSwitch, event.target.checked)}
          inputProps={{ "aria-label": `Toggle ${featureSwitch.name}` }}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: colorActionPrimary,
              "&:hover": { backgroundColor: colorActionPrimaryHover },
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: colorActionPrimary,
            },
          }}
        />
      </Box>
    </TableCell>
  </TableRow>
);
